import * as React from 'react'
import { StoreProps } from '../../../../services/stores/casting.store'
import { I18nService } from '../../../../services/utils/i18n.service'
import { offeredQuantityDiscountsI18nQuantityWithUnit } from '../../../../services/models/offered-quantity-discounts.service'
import { useTheme } from 'styled-components'
import SelectableListComponent from '../../../shared/selectable-list.component'
import CastingTooltipButtonComponent from '../casting-tooltip-button.component'
import { moment } from '../../../../services/utils/moment.service'
import { CastingQuantityDiscountNameComponent } from './casting-quantity-discount-name.component'
import { NeutralButton, PrimaryButton } from '../casting-styled.component'

const i18nScoped = new I18nService(require('./casting-quantity-discount-picker.component.yml'))

export default function CastingQuantityDiscountPickerComponent ({ store, data: { renderingMode, offeredService: os, selectedQuantityDiscount } }: StoreProps) {
  const theme = useTheme()
  const offeredQuantityDiscountChoices = React.useMemo(
    () => os.active_offered_quantity_discounts.slice().sort((a, b) => a.discount_percentage - b.discount_percentage),
    [os.active_offered_quantity_discounts]
  )

  // If the user already has a finalized quantity discount, it applies automatically to the casting.
  if (selectedQuantityDiscount?.validated_at) {
    const expiresSoon = moment(selectedQuantityDiscount.renewable_from).isBefore(moment()) && os.active_offered_quantity_discounts.length > 0

    return <>
      <div style={{ textAlign: 'center', padding: '1em 0' }}>
        <span style={{ display: 'inline-block', width: '8rem', height: '8rem', borderRadius: '50%', backgroundColor: theme.colors.clr_1, color: 'white', fontSize: '2.5em', lineHeight: '8rem' }}>
          { selectedQuantityDiscount.discount_percentage } %
        </span>
      </div>
      
      <div>{ i18nScoped.t('you_have_commitment', { quantity: offeredQuantityDiscountsI18nQuantityWithUnit(selectedQuantityDiscount.quantity_commitment, selectedQuantityDiscount.unit_of_work) }) }</div>

      <div style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
        { i18nScoped.t('you_have_commitment_dates', {
          validated_at: moment(selectedQuantityDiscount.validated_at).format('L'),
          expires_at: moment(selectedQuantityDiscount.expires_at).format('L')
        }) }
      </div>

      { expiresSoon && <>
        <div style={{ color: theme.colors.clr_error, marginBottom: '1em' }}>{ i18nScoped.t('expires_soon') }</div>

        <PrimaryButton
          style={{ display: 'block', marginTop: '0.5em' }}
          children={ i18nScoped.t('renew_quantity_discount') }
          onClick={ () => store.set({ selectedQuantityDiscount: null }) } />

        <PrimaryButton
          style={{ display: 'block', marginTop: '0.5em' }}
          id="js-test-tooltip-next"
          onClick={ () => store.nextFocus({ closeOnMobile: true }) }
          children={ i18nScoped.t('continue_without_renewable') } />
      </> }

      { !expiresSoon && <CastingTooltipButtonComponent
        id="js-test-tooltip-next"
        renderingMode={ renderingMode }
        onClick={ () => store.nextFocus({ closeOnMobile: true }) }
        children={ i18nScoped.t('next') } /> }
    </>
  }

  // If the user has a draft quantity discount, it can revoke it or change it.
  if (selectedQuantityDiscount?.id) {
    return <>
      <div>{ i18nScoped.t('you_have_draft_commitment') }</div>
      <div>{ i18nScoped.t('you_have_draft_commitment_tooltip') }</div>

      <div style={{ padding: '1em', border: `1px solid ${theme.colors.clr_divider}`, borderRadius: 5, margin: '1em 0' }}>
        <CastingQuantityDiscountNameComponent offeredQuantityDiscount={ selectedQuantityDiscount } />
      </div>

      <PrimaryButton
        style={{ display: 'block', marginTop: '0.5em' }}
        children={ i18nScoped.t('remove_quantity_discount') }
        onClick={ () => { store.set({ selectedQuantityDiscount: null }); store.nextFocus({ closeOnMobile: true }) } } />

      <PrimaryButton
        style={{ display: 'block', marginTop: '0.5em' }}
        children={ i18nScoped.t('update_quantity_discount') }
        onClick={ () => store.set({ selectedQuantityDiscount: null }) } />

      <PrimaryButton
        style={{ display: 'block', marginTop: '0.5em' }}
        id="js-test-tooltip-next"
        onClick={ () => store.nextFocus({ closeOnMobile: true }) }
        children={ i18nScoped.t('next_with_discount') } />
    </>
  }

  return <>
    <p>{ i18nScoped.t('please_select_a_discount') }</p>

    <SelectableListComponent
      name="offeredQuantityDiscount"
      value={ os.active_offered_quantity_discounts.find(_ => _.id === selectedQuantityDiscount?.offered_quantity_discount_id) ?? null }
      data={ offeredQuantityDiscountChoices }
      formatter={ value => <CastingQuantityDiscountNameComponent offeredQuantityDiscount={ value } /> }
      onChange={ val => { store.setOfferedQuantityDiscount(val); store.nextFocus({ closeOnMobile: true }) } } />

    <div style={{ fontStyle: 'italic', fontSize: '0.8em', marginBottom: '1em' }}>{ i18nScoped.t('commitment_starts_when_finalized') }</div>

    <NeutralButton
      style={{ display: 'block', marginTop: '0.5em' }}
      onClick={ () => { store.set({ selectedQuantityDiscount: null }); store.nextFocus({ closeOnMobile: true }) } }
      children={ i18nScoped.t('next_no_discount') } />

    <PrimaryButton
      style={{ display: 'block', marginTop: '0.5em' }}
      id="js-test-tooltip-next"
      $disabled={ !selectedQuantityDiscount }
      onClick={ () => store.nextFocus({ closeOnMobile: true }) }
      children={ i18nScoped.t('next_with_discount') } />
  </>
}
